import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BioLinkModal from './BioLinkModal';
import AnalyticsModal from './AnalyticsModal';
import { bioLinkService } from '../../../utils/storage';
import { useToast } from '../../../hooks/useToast';

export default function BioLinkList() {
  const { t } = useTranslation('dashboard');
  const { showToast, ToastContainer } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [bioLinks, setBioLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyticsModal, setAnalyticsModal] = useState<{ isOpen: boolean; bioLink: BioLink | null }>({
    isOpen: false,
    bioLink: null,
  });

  const loadBioLinks = async () => {
    try {
      setLoading(true);
      const data = await bioLinkService.getAll();
      setBioLinks(data);
    } catch (error) {
      console.error('바이오 링크 로드 실패:', error);
      showToast('바이오 링크를 불러오는데 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBioLinks();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      await bioLinkService.delete(id);
      showToast('바이오 링크가 삭제되었습니다', 'success');
      loadBioLinks();
    } catch (error) {
      console.error('바이오 링크 삭제 실패:', error);
      showToast('바이오 링크 삭제에 실패했습니다', 'error');
    }
  };

  const handleCopy = (username: string) => {
    const url = `${window.location.origin}/@${username}`;
    navigator.clipboard.writeText(url);
    showToast('링크가 복사되었습니다', 'success');
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
        <p className="text-gray-600 mt-4">로딩 중...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">바이오 링크 목록</h3>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i>
              {t('bio.create')}
            </button>
          </div>
        </div>

        {bioLinks.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-user-line text-6xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-500 mb-4">아직 생성된 바이오 링크가 없습니다</p>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              첫 바이오 링크 만들기
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {bioLinks.map((bio) => (
              <div key={bio.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-16 h-16 rounded-full flex-shrink-0 overflow-hidden ${
                      bio.avatar_url ? '' : 'bg-gradient-to-br from-teal-400 to-teal-600'
                    }`}>
                      {bio.avatar_url ? (
                        <img src={bio.avatar_url} alt={bio.username} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold">
                          {bio.username[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">@{bio.username}</h4>
                      <p className="text-sm text-gray-600 mb-2">{bio.title}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-links-line"></i>
                          {bio.links?.length || 0} 링크
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {bio.views || 0} 조회
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {new Date(bio.created_at).toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAnalyticsModal({ isOpen: true, bioLink: bio })}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-bar-chart-line mr-2"></i>
                      분석
                    </button>
                    <button
                      onClick={() => window.open(`/@${bio.username}`, '_blank')}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                      title="보기"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                    <button
                      onClick={() => handleCopy(bio.username)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                      title="복사"
                    >
                      <i className="ri-file-copy-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(bio.id)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                      title="삭제"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <BioLinkModal
          onClose={() => setShowModal(false)}
          onSuccess={loadBioLinks}
        />
      )}

      {analyticsModal.bioLink && (
        <AnalyticsModal
          isOpen={analyticsModal.isOpen}
          onClose={() => setAnalyticsModal({ isOpen: false, bioLink: null })}
          itemId={analyticsModal.bioLink.id}
          itemName={analyticsModal.bioLink.username}
          type="bio"
        />
      )}
    </>
  );
}
